(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

  const clampInteger = (value, minimum = 1, maximum = Number.MAX_SAFE_INTEGER) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return minimum;
    return Math.min(Math.max(parsed, minimum), maximum);
  };

  const formatMoney = (cents, format, currency = 'CLP') => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-CL', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'CLP' ? 0 : 2
      }).format(cents / 100);
    } catch (_) {
      return `$${Math.round(cents / 100).toLocaleString('es-CL')}`;
    }
  };

  const initGallery = (section) => {
    const gallery = $('.product-gallery', section);
    const stage = $('[data-gallery-stage]', gallery);
    const mediaItems = $$('[data-product-media]', gallery);
    if (!gallery || !stage || mediaItems.length === 0) return null;

    const mediaControls = $$('[data-media-id]', gallery);
    const previousButton = $('[data-gallery-previous]', gallery);
    const nextButton = $('[data-gallery-next]', gallery);
    const status = $('[data-gallery-status]', gallery);
    let activeIndex = Math.max(mediaItems.findIndex((media) => media.classList.contains('is-active')), 0);
    let touchStart = null;

    const activate = (nextIndex, { announce = true, emit = true } = {}) => {
      const normalizedIndex = (nextIndex + mediaItems.length) % mediaItems.length;
      const activeMedia = mediaItems[normalizedIndex];
      const activeId = activeMedia.dataset.productMedia;
      activeIndex = normalizedIndex;

      mediaItems.forEach((media, index) => {
        const isActive = index === activeIndex;
        media.classList.toggle('is-active', isActive);
        media.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (!isActive) {
          const video = $('video', media);
          if (video) {
            try {
              video.pause();
            } catch (_) {
              // Some externally managed media elements do not expose pause().
            }
          }
        }
      });

      mediaControls.forEach((control) => {
        control.setAttribute('aria-current', control.dataset.mediaId === activeId ? 'true' : 'false');
      });

      gallery.dataset.activeMediaId = activeId;
      if (status) status.textContent = `Contenido ${activeIndex + 1} de ${mediaItems.length}`;

      if (emit) {
        section.dispatchEvent(new CustomEvent('newen:gallery-change', {
          bubbles: true,
          detail: { mediaId: activeId, index: activeIndex, announce }
        }));
      }
    };

    const selectMedia = (mediaId, options) => {
      const nextIndex = mediaItems.findIndex(
        (media) => String(media.dataset.productMedia) === String(mediaId)
      );
      if (nextIndex >= 0) activate(nextIndex, options);
    };

    const move = (direction) => activate(activeIndex + direction);

    mediaControls.forEach((control) => {
      control.addEventListener('click', () => selectMedia(control.dataset.mediaId));
    });
    previousButton?.addEventListener('click', () => move(-1));
    nextButton?.addEventListener('click', () => move(1));

    stage.addEventListener('keydown', (event) => {
      if (event.target !== stage) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    stage.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1 || event.target.closest('button,a,input,select,textarea,video,model-viewer')) {
        touchStart = null;
        return;
      }
      const touch = event.touches[0];
      touchStart = { x: touch.clientX, y: touch.clientY };
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      if (!touchStart || event.changedTouches.length === 0) return;
      const touch = event.changedTouches[0];
      const horizontalDistance = touch.clientX - touchStart.x;
      const verticalDistance = touch.clientY - touchStart.y;
      touchStart = null;

      if (
        Math.abs(horizontalDistance) >= 48
        && Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.15
      ) {
        move(horizontalDistance < 0 ? 1 : -1);
      }
    }, { passive: true });

    activate(activeIndex, { announce: false, emit: false });

    return {
      selectMedia,
      next() {
        move(1);
      },
      previous() {
        move(-1);
      }
    };
  };

  const initQuantityControl = (section) => {
    const input = $('[data-product-quantity]', section);
    if (!input) return;

    $$('[data-quantity-change]', section).forEach((button) => {
      button.addEventListener('click', () => {
        const minimum = clampInteger(input.min || 1, 1);
        const maximum = input.max ? clampInteger(input.max, minimum) : Number.MAX_SAFE_INTEGER;
        const nextValue = clampInteger(Number(input.value) + Number(button.dataset.quantityChange), minimum, maximum);
        input.value = String(nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initBundleSelector = (section, productData) => {
    const selector = $('[data-bundle-selector]', section);
    const quantityInput = $('[data-product-quantity]', section);
    if (!selector || !quantityInput) return null;

    const options = $$('[data-bundle-option]', selector);
    const summary = $('[data-bundle-summary]', selector);
    const packProperty = $('[data-bundle-pack-property]', section);
    const discountProperty = $('[data-bundle-discount-property]', section);
    const totalProperty = $('[data-bundle-total-property]', section);
    const savingProperty = $('[data-bundle-saving-property]', section);
    let unitPrice = Number(selector.dataset.unitPrice || 0);

    const getQuantity = (option) => {
      const customInput = $('[data-bundle-custom-quantity]', option);
      if (!customInput) return clampInteger(option.dataset.quantity, 1);
      const minimum = clampInteger(customInput.min || option.dataset.quantity || 1, 1);
      const maximum = clampInteger(customInput.max || Number.MAX_SAFE_INTEGER, minimum);
      return clampInteger(customInput.value, minimum, maximum);
    };

    const getDiscount = (option) => {
      const parsed = Number(option.dataset.discount || 0);
      return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 100) : 0;
    };

    const calculate = (option) => {
      const quantity = getQuantity(option);
      const isCustomQuantity = option.hasAttribute('data-custom-option');
      const configuredDiscount = isCustomQuantity ? 0 : getDiscount(option);
      const regular = unitPrice * quantity;
      const configuredFixedTotal = Number(option.dataset.fixedTotal || 0);
      const usesFixedTotal = option.dataset.pricingMode === 'fixed_total'
        && Number.isFinite(configuredFixedTotal)
        && configuredFixedTotal > 0;
      const percentageTotal = regular - Math.round(regular * (configuredDiscount / 100));
      const requestedTotal = usesFixedTotal ? configuredFixedTotal : percentageTotal;
      const total = Math.min(Math.max(Math.round(requestedTotal), 0), regular);
      const saving = regular - total;
      const discount = regular > 0 ? Math.round((saving / regular) * 100) : 0;

      return {
        quantity,
        discount,
        configuredDiscount,
        pricingMode: usesFixedTotal ? 'fixed_total' : 'percentage',
        regular,
        saving,
        total
      };
    };

    const updateOptionPrice = (option) => {
      const values = calculate(option);
      const regularNode = $('[data-bundle-regular]', option);
      const totalNode = $('[data-bundle-total]', option);
      const savingNode = $('[data-bundle-saving]', option);
      const discountNode = $('.bundle-option__discount', option);

      if (regularNode) {
        regularNode.textContent = formatMoney(values.regular, productData.money_format, productData.currency);
        regularNode.hidden = values.saving <= 0;
      }
      if (totalNode) totalNode.textContent = formatMoney(values.total, productData.money_format, productData.currency);
      if (savingNode) {
        savingNode.textContent = values.saving > 0
          ? `Ahorras ${formatMoney(values.saving, productData.money_format, productData.currency)}`
          : option.hasAttribute('data-custom-option')
            ? 'Precio normal por unidad'
            : 'Sin descuento';
      }
      if (discountNode) {
        discountNode.textContent = values.discount > 0 ? `-${values.discount}%` : '0%';
        discountNode.setAttribute(
          'aria-label',
          values.discount > 0 ? `${values.discount} por ciento de descuento` : 'Sin descuento'
        );
      }

      return values;
    };

    const syncSelectedOption = (option, announce = true) => {
      options.forEach((item) => {
        const isSelected = item === option;
        item.classList.toggle('is-selected', isSelected);
        const radio = $('[data-bundle-radio]', item);
        if (radio) radio.checked = isSelected;
        const customField = $('[data-bundle-custom-field]', item);
        if (customField) customField.hidden = !isSelected;
      });

      const values = updateOptionPrice(option);
      quantityInput.value = String(values.quantity);
      quantityInput.defaultValue = String(values.quantity);
      quantityInput.setAttribute('value', String(values.quantity));
      if (packProperty) packProperty.value = option.dataset.label || `${values.quantity} unidades`;
      if (discountProperty) discountProperty.value = `${values.discount}%`;
      if (totalProperty) {
        totalProperty.value = formatMoney(values.total, productData.money_format, productData.currency);
      }
      if (savingProperty) {
        savingProperty.value = formatMoney(values.saving, productData.money_format, productData.currency);
      }

      if (summary) {
        const unitLabel = values.quantity === 1 ? 'unidad' : 'unidades';
        const discountLabel = values.discount > 0 ? ` · ${values.discount}% de descuento` : '';
        summary.textContent = `${values.quantity} ${unitLabel} · ${formatMoney(values.total, productData.money_format, productData.currency)}${discountLabel}`;
      }

      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (announce) {
        option.classList.remove('is-just-selected');
        void option.offsetWidth;
        option.classList.add('is-just-selected');
        window.setTimeout(() => option.classList.remove('is-just-selected'), 520);
      }

      const detail = {
        quantity: values.quantity,
        discount: values.discount,
        configuredDiscount: values.configuredDiscount,
        pricingMode: values.pricingMode,
        regular: values.regular,
        saving: values.saving,
        total: values.total,
        label: option.dataset.label || ''
      };
      window.NewenBundleSelection = detail;
      section.dispatchEvent(new CustomEvent('newen:bundle-change', { bubbles: true, detail }));
    };

    options.forEach((option) => {
      updateOptionPrice(option);
      const radio = $('[data-bundle-radio]', option);
      radio?.addEventListener('change', () => {
        if (radio.checked) syncSelectedOption(option);
      });
      option.addEventListener('click', (event) => {
        if (event.target.closest('[data-bundle-custom-quantity]')) return;
        syncSelectedOption(option);
      });

      const customInput = $('[data-bundle-custom-quantity]', option);
      customInput?.addEventListener('input', () => {
        const minimum = clampInteger(customInput.min || 1, 1);
        const maximum = clampInteger(customInput.max || Number.MAX_SAFE_INTEGER, minimum);
        customInput.value = String(clampInteger(customInput.value, minimum, maximum));
        option.dataset.quantity = customInput.value;
        if (option.classList.contains('is-selected')) syncSelectedOption(option);
        else updateOptionPrice(option);
      });
    });

    const initial = options.find((option) => $('[data-bundle-radio]', option)?.checked) || options[0];
    if (initial) syncSelectedOption(initial, false);

    return {
      sync() {
        const selected = options.find((option) => option.classList.contains('is-selected')) || options[0];
        if (selected) syncSelectedOption(selected, false);
      },
      updatePrice(nextUnitPrice) {
        unitPrice = Number(nextUnitPrice || 0);
        selector.dataset.unitPrice = String(unitPrice);
        options.forEach(updateOptionPrice);
        const selected = options.find((option) => option.classList.contains('is-selected')) || options[0];
        if (selected) syncSelectedOption(selected, false);
      }
    };
  };

  const initProduct = (section) => {
    if (section.dataset.productInitialized === 'true') return;
    section.dataset.productInitialized = 'true';

    const dataNode = $('[data-product-json]', section);
    if (!dataNode) return;

    let productData;
    try {
      productData = JSON.parse(dataNode.textContent);
    } catch (_) {
      return;
    }

    const gallery = initGallery(section);
    initQuantityControl(section);
    const bundleSelector = initBundleSelector(section, productData);
    const productForm = $('.product-form--cod', section);
    const syncBeforePurchase = (event) => {
      const purchaseControl = event.target.closest(
        'button,[role="button"],input[type="submit"],[class*="_rsi-cod-form"]'
      );
      if (purchaseControl) bundleSelector?.sync();
    };

    section.addEventListener('pointerdown', syncBeforePurchase, true);
    section.addEventListener('click', syncBeforePurchase, true);
    section.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') syncBeforePurchase(event);
    }, true);
    productForm?.addEventListener('submit', () => bundleSelector?.sync(), true);

    const variantControl = $('[data-variant-select]', section);
    const masterVariant = $('[data-master-variant]', section);
    const price = $('[data-product-price]', section);
    const compare = $('[data-product-compare]', section);
    const availability = $('[data-availability]', section);
    const submitButton = $('[data-product-submit]', section);
    const submitLabel = $('[data-product-submit-label]', section);

    const updateVariant = () => {
      const selectedId = variantControl?.value || masterVariant?.value;
      const variant = productData.variants.find((item) => String(item.id) === String(selectedId))
        || productData.variants[0];
      if (!variant) return;

      if (masterVariant) {
        masterVariant.value = String(variant.id);
        masterVariant.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (section.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (price) price.textContent = formatMoney(variant.price, productData.money_format, productData.currency);
      if (compare) {
        const hasComparePrice = Number(variant.compare_at_price || 0) > Number(variant.price);
        compare.textContent = hasComparePrice
          ? formatMoney(variant.compare_at_price, productData.money_format, productData.currency)
          : '';
        compare.hidden = !hasComparePrice;
      }
      if (availability) {
        availability.textContent = variant.available
          ? availability.dataset.inStock
          : availability.dataset.outOfStock;
        availability.classList.toggle('availability--out', !variant.available);
      }
      if (submitButton) submitButton.disabled = !variant.available;
      if (submitLabel) submitLabel.textContent = variant.available ? 'Paga al recibir' : 'Agotado';

      bundleSelector?.updatePrice(variant.price);

      const featuredMediaId = variant.featured_media?.id;
      if (featuredMediaId) gallery?.selectMedia(featuredMediaId);

      document.dispatchEvent(new CustomEvent('newen:variant-change', {
        detail: { variant }
      }));
    };

    variantControl?.addEventListener('change', updateVariant);
    updateVariant();
  };

  const initAll = (root = document) => {
    if (root.matches?.('[data-product-section]')) initProduct(root);
    $$('[data-product-section]', root).forEach(initProduct);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
